/**
 * A per-sub-view error boundary for the Patient Context Wiki.
 *
 * Defense-in-depth: the WikiTab wraps the whole workspace in one
 * {@link ErrorBoundary}, but that boundary blanks the *entire* tab when any
 * single sub-view throws (e.g. a bad API shape). Wrapping each sub-view in its
 * own boundary contains the failure to that view — the subtab strip and the
 * other views stay usable while the broken view degrades to an in-place notice.
 * The thrown error is routed through the shared logger (which ships to the
 * backend client-log ingest); no rendered props / PHI are forwarded.
 */
import { Component, ReactNode } from "react";
import logger from "../../utils/logger";

/** Props for {@link WikiViewBoundary}. */
interface WikiViewBoundaryProps {
  /** A PHI-free label for the wrapped sub-view (used only for logging). */
  view: string;
  /** The sub-view to render. */
  children: ReactNode;
}

/** Error-capture state for {@link WikiViewBoundary}. */
interface WikiViewBoundaryState {
  /** Whether the wrapped sub-view has thrown. */
  hasError: boolean;
}

/**
 * Contain a single wiki sub-view's render error to that view.
 *
 * @returns The wrapped sub-view, or an in-place degraded notice on error.
 */
export class WikiViewBoundary extends Component<
  WikiViewBoundaryProps,
  WikiViewBoundaryState
> {
  public state: WikiViewBoundaryState = { hasError: false };

  /**
   * Flip into the error state when a child throws.
   *
   * @returns The next state with the error flag set.
   */
  public static getDerivedStateFromError(): WikiViewBoundaryState {
    return { hasError: true };
  }

  /**
   * Log the captured error (no PHI), keyed by the sub-view label.
   *
   * @param error - The error thrown by a descendant.
   */
  public componentDidCatch(error: Error): void {
    logger.error(`Wiki sub-view failed to render: ${this.props.view}`, error);
  }

  /**
   * Render the sub-view, or a contained fallback when it has thrown.
   *
   * @returns The children, or the degraded in-place notice.
   */
  public render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
        >
          This view couldn&apos;t be displayed. Try another tab or refresh.
        </div>
      );
    }
    return this.props.children;
  }
}

export default WikiViewBoundary;
